import { Box, Fade, Icon, Typography } from "@mui/material";
import companyPlaceholder from "@/assets/company-placeholder.png";
import { useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IEmpresa } from "@/redux/models/empresaTypes";
import AutocompleteEmpresas from "../../../../../../components/AutocompleteEmpresas";
import { EmpresaContext } from "../VerEmpresasPage";
import { LoadingHeaderEmpresa } from "./LoadingHeaderEmpresa";

const ChipEmpresa = ({ image, text }: { image: string; text: string }) => (
    <Box
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            mr: 1.5,
            py: 0.6,
            px: 1.2,
            borderRadius: '16px', // Bordes más redondeados
            bgcolor: 'grey.100',
            color: 'grey.800',
            fontSize: '0.85rem',
            fontWeight: 500,
            transition: 'transform 0.2s ease, opacity 0.2s ease',
            '&:hover': {
                transform: 'scale(1.05)',
                opacity: 0.9,
                bgcolor: 'grey.200',
            },
        }}
    >
        <Icon sx={{ color: 'grey.600', mr: 0.5, fontSize: '1.1rem' }}>{image}</Icon>
        {text}
    </Box>
);

const QuickAccessChip = ({ empresa, onClick }: { empresa: IEmpresa; onClick: (empresa: IEmpresa) => void }) => (
    <Box
        onClick={() => onClick(empresa)}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            mr: 1,
            py: 0.5,
            px: 1.5,
            borderRadius: '16px',
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
            fontSize: '0.85rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: (theme) => `0 2px 8px ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)'}`,
            },
        }}
    >
        {empresa.nombre}
    </Box>
);

const HeaderEmpresa = ({ empresas, loadingEmpresas }: { empresas?: IEmpresa[]; loadingEmpresas: boolean }) => {
    const { empresa, setEmpresa } = useContext(EmpresaContext);
    const setSearchParams = useSearchParams()[1];
    const [currentImage, setCurrentImage] = useState(companyPlaceholder);

    const handleChangeEmpresa = (newEmpresa: IEmpresa | null) => {
        if (newEmpresa) {
            setEmpresa && setEmpresa(newEmpresa);
            setSearchParams({ empresa: newEmpresa.id.toString() });
            if (newEmpresa.foto && newEmpresa.foto !== currentImage) {
                setCurrentImage(newEmpresa.foto);
            }
        }
    };

    if (loadingEmpresas) {
        return <LoadingHeaderEmpresa />;
    }

    if (!empresa) {
        return (
            <Fade in timeout={1000}>
                <Box
                    sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        bgcolor: (theme) =>
                            theme.palette.mode === 'light'
                                ? 'linear-gradient(135deg, #e8f0fe 0%, #d9e8ff 100%)'
                                : 'linear-gradient(135deg, #1c2526 0%, #2a3b3c 100%)',
                        boxShadow: (theme) =>
                            theme.palette.mode === 'light'
                                ? '0 6px 16px rgba(0,0,0,0.12)'
                                : '0 6px 16px rgba(255,255,255,0.08)',
                        textAlign: 'center',
                        maxWidth: 600,
                        mx: 'auto',
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 600, mb: 2, color: 'text.primary' }}
                    >
                        Selecciona una empresa
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ fontSize: '0.95rem', color: 'text.secondary', mb: 3, maxWidth: '400px', mx: 'auto' }}
                    >
                        Elige una empresa para ver sus detalles y gestionar su información.
                    </Typography>
                    <AutocompleteEmpresas
                        rows={empresas || []}
                        loading={loadingEmpresas}
                        empresa={null}
                        onChange={handleChangeEmpresa}
                        sx={{
                            maxWidth: '400px',
                            mx: 'auto',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            '&:hover': {
                                transform: 'scale(1.03)',
                                boxShadow: (theme) => `0 2px 8px ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
                            },
                        }}
                    />
                    {empresas && empresas.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                            {empresas.slice(0, 3).map((emp) => (
                                <QuickAccessChip key={emp.id} empresa={emp} onClick={handleChangeEmpresa} />
                            ))}
                        </Box>
                    )}
                </Box>
            </Fade>
        );
    }

    return (
        <Fade in={!loadingEmpresas} timeout={1000}>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 2, sm: 3 },
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    bgcolor: (theme) =>
                        theme.palette.mode === 'light'
                            ? 'linear-gradient(135deg, #e8f0fe 0%, #d9e8ff 100%)'
                            : 'linear-gradient(135deg, #1c2526 0%, #2a3b3c 100%)',
                    boxShadow: (theme) =>
                        theme.palette.mode === 'light'
                            ? '0 6px 16px rgba(0,0,0,0.12)'
                            : '0 6px 16px rgba(255,255,255,0.08)',
                    border: (theme) => `1px solid ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'}`,
                }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'scale(1.03)' },
                    }}
                >
                    <Fade in key={empresa.id} timeout={500}>
                        <img
                            src={currentImage}
                            alt={empresa.nombre}
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                filter: 'blur(1px)',
                                transition: 'filter 0.5s ease',
                            }}
                            onLoad={(e) => (e.currentTarget.style.filter = 'none')}
                            onError={(e: any) => (e.target.src = companyPlaceholder)}
                        />
                    </Fade>
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: '300px' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.3rem', sm: '1.6rem' },
                                fontWeight: 700,
                                color: 'text.primary',
                                minWidth: '200px',
                                letterSpacing: -0.2,
                            }}
                        >
                            {empresa.nombre}
                        </Typography>
                        <AutocompleteEmpresas
                            rows={empresas || []}
                            loading={loadingEmpresas}
                            empresa={empresa}
                            onChange={handleChangeEmpresa}
                            sx={{
                                minWidth: { xs: '100%', sm: '250px' },
                                maxWidth: '400px',
                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                '&:hover': {
                                    transform: 'scale(1.03)',
                                    boxShadow: (theme) => `0 2px 8px ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
                                },
                            }}
                        />
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '0.95rem',
                            color: 'text.secondary',
                            mb: 2,
                            maxWidth: '600px',
                            lineHeight: 1.6,
                        }}
                    >
                        {empresa.descripcion || ''}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                        {empresa.direccion && <ChipEmpresa image="explore" text={empresa.direccion} />}
                        {empresa.telefono && <ChipEmpresa image="phone" text={empresa.telefono} />}
                        {empresa.email && <ChipEmpresa image="email" text={empresa.email} />}
                        {empresa.usuarios && (
                            <ChipEmpresa image="people" text={`Usuarios ${empresa.usuarios.length}`} />
                        )}
                    </Box>
                    {empresas && empresas.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {empresas
                                .filter((emp) => emp.id !== empresa.id)
                                .slice(0, 3)
                                .map((emp) => (
                                    <QuickAccessChip key={emp.id} empresa={emp} onClick={handleChangeEmpresa} />
                                ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Fade>
    );
};

export default HeaderEmpresa;